import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';

import { take } from 'rxjs';

import { BusinessHours } from '@hdms/shared/page-frame/data-access';
import { Tab, TabService } from '@hdms/shared/ui-core';
import { FeatureToggleService, fadeOnAddRemove } from '@hdms/shared/util-core';

import { ContactTabType } from '../../../config/header-config.const';
import {
  FT_HEADER_CALL_BACK_ENABLED,
  FT_HEADER_CHAT_ENABLED,
  FT_HEADER_SEND_MESSAGE_ENABLED
} from '../../../config/toggles';
import { ContactComponentAbstract } from '../contact-abstract/contact-component.abstract';
import { ContactBodyLoaderService } from '../contact-body/contact-body-loader.service';

@Component({
  selector: 'shrd-pf-ft-contact-header',
  templateUrl: './contact-header.component.html',
  styleUrls: ['./contact-header.component.scss'],
  animations: [fadeOnAddRemove('tabsFade', 0.3)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ContactHeaderComponent extends ContactComponentAbstract implements OnInit {
  @HostBinding('class.shrd-pf-ft-contact-header') readonly hostClass = true;

  readonly loaded$ = inject(ContactBodyLoaderService).loaded$;
  private readonly tabService = inject(TabService);
  private readonly isLiveChatFtEnabled = inject(FeatureToggleService).getToggleById(FT_HEADER_CHAT_ENABLED);
  private readonly isCallBackFtEnabled = inject(FeatureToggleService).getToggleById(FT_HEADER_CALL_BACK_ENABLED);
  private readonly isSendMessageFtEnabled = inject(FeatureToggleService).getToggleById(FT_HEADER_SEND_MESSAGE_ENABLED);

  @Input() inModal = false;
  @Input() activeTab = ContactTabType.SELF_SERVICE;

  ngOnInit(): void {
    this.updateTabs();

    // hide tabset if there's only one tab
    if (this.tabs.length === 1) {
      const tab = new Tab();
      tab.id = this.tabs[0];
      tab.contentReference = `${this.tabContentIdPrefix}${tab.id}`;
      tab.active = true;
      this.tabService.register(tab);
      this.tabService.setActive(tab);
    }
  }

  private updateTabs(): void {
    // remove contact tabs if they're not enabled
    this.loaded$.pipe(take(1)).subscribe(businessHours => {
      this.tabs = this.tabs.filter(tab => this.isTabEnabled(tab, businessHours));
    });
  }

  private isTabEnabled(tab: ContactTabType, businessHours: BusinessHours): boolean {
    switch (tab) {
      case ContactTabType.LIVE_CHAT:
        return businessHours.liveChatEnabled && this.isLiveChatFtEnabled;
      case ContactTabType.CALL_BACK:
        return businessHours.callMeBackEnabled && this.isCallBackFtEnabled;
      case ContactTabType.SEND_MESSAGE:
        return businessHours.sendMessageEnabled && this.isSendMessageFtEnabled;
      case ContactTabType.FIND_STORE:
        return businessHours.storeLocatorEnabled;
      default:
        return true;
    }
  }
}
